import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Event, UpdateEventRequest } from '../utils/types';
import { apiGet, apiPut, ApiError } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

const EditEvent: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const [event, setEvent] = useState<Event | null>(null);
  const [formData, setFormData] = useState<UpdateEventRequest>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  const { token, user } = useAuth();
  const navigate = useNavigate();

  // Check if current user has organizer role
  // Check if current user has organizer role (case-insensitive)
  const isOrganizer = user?.roles?.some(role => role.toUpperCase() === 'ORGANIZER') || false;

  useEffect(() => {
    if (eventId && token) {
      loadEvent();
    }
  }, [eventId, token]);

  // Redirect non-organizers
  useEffect(() => {
    if (user && !isOrganizer) {
      navigate('/events');
    }
  }, [user, isOrganizer, navigate]);

  const loadEvent = async () => {
    if (!eventId || !token) return;
    
    setLoading(true);
    setError('');
    
    try {
      const eventData = await apiGet<Event>(`/events/${eventId}`, token);
      setEvent(eventData);
      
      // Initialize form data with current event data
      setFormData({
        name: eventData.name,
        description: eventData.description,
        state: eventData.state,
        max_team_size: eventData.max_team_size,
        registration_deadline: formatDateTimeLocal(eventData.registration_deadline),
        submission_deadline_overview: formatDateTimeLocal(eventData.submission_deadline_overview),
        submission_deadline_media: formatDateTimeLocal(eventData.submission_deadline_media)
      });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('Failed to load event');
      }
    } finally {
      setLoading(false);
    }
  };

  // Format datetime for HTML datetime-local input
  const formatDateTimeLocal = (dateString: string): string => {
    const date = new Date(dateString);
    // Get timezone offset and adjust
    const offset = date.getTimezoneOffset();
    const adjustedDate = new Date(date.getTime() - (offset * 60 * 1000));
    return adjustedDate.toISOString().slice(0, 16);
  };

  // Convert datetime-local input back to ISO string
  const formatToISO = (dateTimeLocal: string): string => {
    return new Date(dateTimeLocal).toISOString();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'max_team_size' ? Number(value) : value
    }));
    setError(''); // Clear error when user makes changes
    setSuccessMessage(''); // Clear success message
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!token || !eventId) {
      setError('You must be logged in to edit an event');
      return;
    }

    // Basic validation
    if (!formData.name?.trim()) {
      setError('Event name is required');
      return;
    }
    
    if (!formData.description?.trim()) {
      setError('Event description is required');
      return;
    }

    if ((formData.max_team_size ?? 0) < 1) {
      setError('Maximum team size must be at least 1');
      return;
    }

    // Validate dates if provided
    if (formData.registration_deadline && formData.submission_deadline_overview) {
      const regDeadline = new Date(formData.registration_deadline);
      const overviewDeadline = new Date(formData.submission_deadline_overview);
      
      if (regDeadline >= overviewDeadline) {
        setError('Registration deadline must be before overview submission deadline');
        return;
      }
    }
    
    if (formData.submission_deadline_overview && formData.submission_deadline_media) {
      const overviewDeadline = new Date(formData.submission_deadline_overview);
      const mediaDeadline = new Date(formData.submission_deadline_media);
      
      if (overviewDeadline >= mediaDeadline) {
        setError('Overview submission deadline must be before media submission deadline');
        return;
      }
    }

    setSaving(true);
    setError('');
    setSuccessMessage('');

    try {
      // Prepare update data, converting datetime-local inputs to ISO strings
      const updateData: UpdateEventRequest = {
        name: formData.name,
        description: formData.description,
        state: formData.state,
        max_team_size: formData.max_team_size,
        registration_deadline: formData.registration_deadline ? formatToISO(formData.registration_deadline) : undefined,
        submission_deadline_overview: formData.submission_deadline_overview ? formatToISO(formData.submission_deadline_overview) : undefined,
        submission_deadline_media: formData.submission_deadline_media ? formatToISO(formData.submission_deadline_media) : undefined
      };

      const updatedEvent = await apiPut<Event>(`/events/${eventId}`, updateData, token);
      setEvent(updatedEvent);
      setSuccessMessage('Event updated successfully!');
      
      // If state was changed, provide feedback
      if (formData.state !== event?.state) {
        const stateMessages = {
          open: 'Event is now open for registration!',
          judging: 'Event is now in judging phase.',
          completed: 'Event has been marked as completed.'
        };
        if (formData.state && formData.state in stateMessages) {
          setSuccessMessage(`${stateMessages[formData.state as keyof typeof stateMessages]} Event updated successfully!`);
        }
      }
      
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('Failed to update event');
      }
    } finally {
      setSaving(false);
    }
  };

  // Don't render the form for non-organizers
  if (user && !isOrganizer) {
    return (
      <div className="container" style={{ textAlign: 'center', padding: '40px' }}>
        <h2>Access Denied</h2>
        <p>Only organizers can edit events.</p>
        <Link to="/events" className="btn btn-secondary">Back to Events</Link>
      </div>
    );
  }

  if (loading) {
    return <div className="loading">Loading event...</div>;
  }

  if (error && !event) {
    return (
      <div className="error">
        {error}
        <Link to="/events" className="btn btn-secondary" style={{ marginLeft: '10px' }}>
          Back to Events
        </Link>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="card" style={{ textAlign: 'center' }}>
        <h3>Event not found</h3>
        <Link to="/events" className="btn">Back to Events</Link>
      </div>
    );
  }

  // Check if event can be edited (only draft events should allow full editing)
  const canEdit = event.state === 'draft';
  const canChangeState = event.state === 'draft'; // Can promote from draft to open

  return (
    <div style={{ maxWidth: '800px', margin: '0 auto' }}>
      <div style={{ marginBottom: '20px' }}>
        <Link to={`/events/${eventId}`} className="btn btn-secondary">← Back to Event</Link>
      </div>
      
      <div className="card">
        <h1>Edit Event: {event.name}</h1>
        
        {successMessage && (
          <div style={{ 
            padding: '10px', 
            backgroundColor: '#d4edda', 
            color: '#155724', 
            border: '1px solid #c3e6cb', 
            borderRadius: '4px', 
            marginBottom: '20px' 
          }}>
            {successMessage}
          </div>
        )}
        
        {error && (
          <div style={{ 
            padding: '10px', 
            backgroundColor: '#f8d7da', 
            color: '#721c24', 
            border: '1px solid #f5c6cb', 
            borderRadius: '4px', 
            marginBottom: '20px' 
          }}>
            {error}
          </div>
        )}

        {!canEdit && (
          <div style={{ 
            padding: '10px', 
            backgroundColor: '#fff3cd', 
            color: '#856404', 
            border: '1px solid #ffeaa7', 
            borderRadius: '4px', 
            marginBottom: '20px' 
          }}>
            ⚠️ This event is in "{event.state}" state. Only draft events can be fully edited.
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="name">Event Name *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name || ''}
              onChange={handleInputChange}
              required
              maxLength={200}
              disabled={!canEdit}
              style={{ opacity: canEdit ? 1 : 0.6 }}
            />
          </div>

          <div className="form-group">
            <label htmlFor="description">Description *</label>
            <textarea
              id="description"
              name="description"
              value={formData.description || ''}
              onChange={handleInputChange}
              required
              maxLength={2000}
              rows={4}
              disabled={!canEdit}
              style={{ opacity: canEdit ? 1 : 0.6 }}
            />
          </div>

          <div className="form-group">
            <label htmlFor="max_team_size">Maximum Team Size *</label>
            <input
              type="number"
              id="max_team_size"
              name="max_team_size"
              value={formData.max_team_size ?? 5}
              onChange={handleInputChange}
              min={1}
              required
              disabled={!canEdit}
              style={{ opacity: canEdit ? 1 : 0.6 }}
            />
            <small style={{ color: '#666', display: 'block', marginTop: '5px' }}>
              Maximum number of members allowed on each team for this event
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="state">Event State</label>
            <select
              id="state"
              name="state"
              value={formData.state || 'draft'}
              onChange={handleInputChange}
              disabled={!canChangeState}
              style={{ opacity: canChangeState ? 1 : 0.6 }}
            >
              <option value="draft">Draft</option>
              <option value="open">Open (Ready for Registration)</option>
              <option value="judging">Judging</option>
              <option value="completed">Completed</option>
            </select>
            {canChangeState && (
              <small style={{ color: '#666', display: 'block', marginTop: '5px' }}>
                💡 Set to "Open" when ready for participants to register
              </small>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="registration_deadline">Registration Deadline *</label>
            <input
              type="datetime-local"
              id="registration_deadline"
              name="registration_deadline"
              value={formData.registration_deadline || ''}
              onChange={handleInputChange}
              required
              disabled={!canEdit}
              style={{ opacity: canEdit ? 1 : 0.6 }}
            />
          </div>

          <div className="form-group">
            <label htmlFor="submission_deadline_overview">Overview Submission Deadline *</label>
            <input
              type="datetime-local"
              id="submission_deadline_overview"
              name="submission_deadline_overview"
              value={formData.submission_deadline_overview || ''}
              onChange={handleInputChange}
              required
              disabled={!canEdit}
              style={{ opacity: canEdit ? 1 : 0.6 }}
            />
          </div>

          <div className="form-group">
            <label htmlFor="submission_deadline_media">Media Submission Deadline *</label>
            <input
              type="datetime-local"
              id="submission_deadline_media"
              name="submission_deadline_media"
              value={formData.submission_deadline_media || ''}
              onChange={handleInputChange}
              required
              disabled={!canEdit}
              style={{ opacity: canEdit ? 1 : 0.6 }}
            />
          </div>

          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <Link to={`/events/${eventId}`} className="btn btn-secondary">
              Cancel
            </Link>
            <button 
              type="submit" 
              className="btn btn-primary"
              disabled={saving || (!canEdit && !canChangeState)}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditEvent;
